import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import api from '../../../utils/api';

const ExportModal = ({ isOpen, onClose, onExport, reportName, categoryFilter }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeRawData, setIncludeRawData] = useState(false);
  const [scheduleExport, setScheduleExport] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [exportError, setExportError] = useState('');

  const formatOptions = [
    { value: 'csv', label: 'CSV File' },
    { value: 'json', label: 'JSON Data' }
  ];

  const handleExport = async () => {
    setExportError('');
    if (scheduleExport) {
      try {
        const recipients = emailRecipients?.split(',')?.map(e => e.trim()).filter(Boolean);
        await api.post('/export/email', {
          recipients,
          reportName,
          category: categoryFilter === 'all' ? undefined : categoryFilter
        });
        onClose();
      } catch (e) {
        console.error('Email export failed', e);
        setExportError('The CSV could not be emailed. Please try again.');
      }
      return;
    }

    try {
      await onExport({ format: exportFormat, includeCharts, includeRawData, reportName });
      onClose();
    } catch (e) {
      console.error('Export failed', e);
      setExportError('The report could not be exported. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Export Report</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">Report: {reportName}</h3>
            <p className="text-sm text-muted-foreground">Configure export settings and delivery options</p>
          </div>

          <div>
            <Select
              label="Export Format"
              options={formatOptions}
              value={exportFormat}
              onChange={(format) => {
                setExportFormat(format);
                if (format !== 'csv') setScheduleExport(false);
              }}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Include Options</h4>
            <Checkbox
              label="Include charts and visualizations"
              checked={includeCharts}
              onChange={(e) => setIncludeCharts(e?.target?.checked)}
            />
            <Checkbox
              label="Include raw data tables"
              checked={includeRawData}
              onChange={(e) => setIncludeRawData(e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            {exportFormat === 'csv' && (
              <Checkbox
                label="Email this CSV now"
                checked={scheduleExport}
                onChange={(e) => setScheduleExport(e?.target?.checked)}
              />
            )}
            {scheduleExport && (
              <div className="space-y-4 pl-6 border-l-2 border-border">
                <Input
                  label="Email Recipients"
                  placeholder="email1@company.com, email2@company.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e?.target?.value)}
                  description="Separate multiple emails with commas. Leave blank to send to your account email."
                />
              </div>
            )}
            {exportError && <p role="alert" className="text-sm text-destructive">{exportError}</p>}
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleExport}
            iconName="Download"
            iconPosition="left"
            iconSize={16}
          >
            {scheduleExport ? 'Email CSV' : 'Export Now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;